/**
 * Display helpers for the recurring `MM-DD` ranges a camping ground stores in
 * `publicData.seasonalPrices` and `publicData.closedPeriods`.
 *
 * The matching logic lives in `seasonalPricing.js`, which is kept byte-identical
 * with its server twin and therefore must stay free of presentation concerns —
 * hence this separate module.
 */

/**
 * A leap year, used only as a carrier so `02-29` formats as February 29 instead
 * of overflowing into March 1. The year itself is never rendered.
 */
const FORMATTING_YEAR = 2024;

const MONTH_DAY_FORMAT = { day: 'numeric', month: 'long', timeZone: 'UTC' };

/** Stable identity of a range. Ranges cannot overlap, so start+end is unique per listing. */
export const periodKey = ({ start, end }) => `${start}_${end}`;

/**
 * `MM-DD` as a UTC `Date` in a leap year, or `null` when the value is malformed.
 *
 * @param {string} monthDay - Recurring `MM-DD` string
 * @returns {Date|null}
 */
export const monthDayToDate = (monthDay) => {
  const [rawMonth, rawDay] = `${monthDay || ''}`.split('-');
  const month = Number(rawMonth);
  const day = Number(rawDay);
  if (!month || month < 1 || month > 12 || !day || day < 1 || day > 31) {
    return null;
  }
  const date = new Date(Date.UTC(FORMATTING_YEAR, month - 1, day));
  // Date.UTC rolls overflow forward (02-30 -> March 1), which would silently
  // render a day the host never picked.
  return date.getUTCMonth() === month - 1 ? date : null;
};

/**
 * Localised day-and-month, e.g. `1. Juni`. Empty string for malformed input.
 *
 * @param {object} intl - react-intl intl object
 * @param {string} monthDay - Recurring `MM-DD` string
 * @returns {string}
 */
export const formatMonthDay = (intl, monthDay) => {
  const date = monthDayToDate(monthDay);
  return date ? intl.formatDate(date, MONTH_DAY_FORMAT) : '';
};

/**
 * Localised inclusive range, e.g. `1. Juni – 31. August`.
 *
 * @param {object} intl - react-intl intl object
 * @param {{start: string, end: string}} period
 * @returns {string}
 */
export const formatMonthDayRange = (intl, { start, end }) => {
  const from = formatMonthDay(intl, start);
  const to = formatMonthDay(intl, end);
  if (!from || !to) {
    return '';
  }
  return from === to ? from : `${from} – ${to}`;
};

/**
 * Renderable rows for a list of periods, in the order the host arranged them.
 * Malformed entries are dropped. The host's free text is taken from the
 * translation map when one is available and falls back to the original.
 *
 * @param {Array<object>} periods - `publicData.seasonalPrices` or `publicData.closedPeriods`
 * @param {object} [translations] - Locale bucket keyed by `periodKey`
 * @param {string} textField - `'label'` for seasons, `'note'` for closing periods
 * @returns {Array<object>} The original entries plus `key` and `text`
 */
export const buildPeriodRows = (periods, translations, textField) =>
  (periods || [])
    .filter((period) => period && monthDayToDate(period.start) && monthDayToDate(period.end))
    .map((period) => {
      const key = periodKey(period);
      return {
        ...period,
        key,
        text: translations?.[key]?.[textField] || period[textField] || '',
      };
    });

/**
 * The free texts of a period list, keyed for the translation store. Entries
 * without text are omitted so nothing empty is sent to Google Translate.
 *
 * An empty result is meaningful: it tells `server/api/translation/put.js` to
 * prune every stored text for that map.
 *
 * @param {Array<object>} periods
 * @param {string} textField - `'label'` for seasons, `'note'` for closing periods
 * @returns {object} `{ '06-01_08-31': { label: 'Hochsaison' } }`
 */
export const periodTextsForTranslation = (periods, textField) => {
  const texts = {};
  for (const period of periods || []) {
    const value = period?.[textField];
    if (period?.start && period?.end && typeof value === 'string' && value.trim().length > 0) {
      texts[periodKey(period)] = { [textField]: value };
    }
  }
  return texts;
};
